import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Bell, 
  X, 
  Check, 
  Clock, 
  AlertTriangle, 
  CreditCard,
  Users,
  Target,
  TrendingDown,
  UserPlus,
  Server,
  ChevronRight
} from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { useAlerts, useAlertStats, useResolveAlert, useSnoozeAlert } from '@/hooks/useApi'
import { toastSuccess, toastError } from '@/components/ui'

const ALERT_ICONS = {
  payment_failed: CreditCard,
  payment_late: CreditCard,
  contract_expiring: Clock,
  client_silent: Users,
  campaign_underperforming: TrendingDown,
  prospect_stale: Target,
  system_error: Server,
  capacity_warning: AlertTriangle,
  churn_risk: AlertTriangle,
  onboarding_stuck: UserPlus,
}

const SEVERITY_STYLES = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    badge: 'bg-red-500'
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-500',
    badge: 'bg-amber-500'
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    badge: 'bg-blue-500'
  }
}

function AlertItem({ alert, onResolve, onSnooze, onNavigate }) {
  const [showActions, setShowActions] = useState(false)
  const Icon = ALERT_ICONS[alert.alert_type] || AlertTriangle
  const styles = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info
  
  const timeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  return (
    <div 
      className={cn(
        'p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-100 cursor-pointer relative group',
      )}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => alert.action_url && onNavigate(alert.action_url)}
    >
      <div className="flex gap-3">
        <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', styles.bg)}>
          <Icon className={cn('w-4 h-4', styles.icon)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-sm text-gray-900 truncate">{alert.title}</div>
            <div className="text-xs text-gray-400 shrink-0">{timeAgo(alert.created_at)}</div>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{alert.description}</div>
        </div>
      </div>
      
      {/* Quick actions */}
      {showActions && (
        <div 
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white shadow-xs border border-gray-200 rounded-lg p-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onResolve(alert.id)}
            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors duration-100"
            title="Resolve"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onSnooze(alert.id)}
            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors duration-100"
            title="Snooze 24h"
          >
            <Clock className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}

export function AlertsDropdown() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  
  const { data: alertsData, refetch } = useAlerts({ limit: 10 })
  const { data: statsData } = useAlertStats()
  const resolveAlert = useResolveAlert()
  const snoozeAlert = useSnoozeAlert()
  
  const alerts = alertsData?.alerts || []
  const stats = statsData?.stats || {}
  const totalUnresolved = stats.total_unresolved || 0
  const criticalCount = stats.critical_count || 0
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const handleResolve = async (alertId) => {
    try {
      await resolveAlert.mutate(alertId, { resolved_by: 'admin' })
      toastSuccess('Alert resolved')
      refetch()
    } catch (err) {
      toastError('Failed to resolve', err.message)
    }
  }
  
  const handleSnooze = async (alertId) => {
    try {
      await snoozeAlert.mutate(alertId, 24)
      toastSuccess('Snoozed for 24 hours')
      refetch()
    } catch (err) {
      toastError('Failed to snooze', err.message)
    }
  }
  
  const handleNavigate = (url) => {
    setIsOpen(false)
    navigate(url)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'p-2 relative transition-colors duration-100',
          isOpen ? 'text-(--accent)' : 'text-(--muted-foreground) hover:text-(--accent)'
        )}
      >
        <Bell className="w-5 h-5" />
        {totalUnresolved > 0 && (
          <span className={cn(
            'absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center',
            'text-[10px] font-bold text-white rounded-full px-1',
            criticalCount > 0 ? 'bg-red-500' : 'bg-amber-500'
          )}>
            {totalUnresolved > 99 ? '99+' : totalUnresolved}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="font-semibold text-gray-900">Alerts</h3>
              {totalUnresolved > 0 && (
                <p className="text-xs text-gray-500">
                  {criticalCount > 0 && <span className="text-red-600 font-medium">{criticalCount} critical</span>}
                  {criticalCount > 0 && stats.warning_count > 0 && ' • '}
                  {stats.warning_count > 0 && <span className="text-amber-600">{stats.warning_count} warning</span>}
                </p>
              )}
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Alerts list */}
          <div className="max-h-[400px] overflow-y-auto">
            {alerts.length > 0 ? (
              alerts.map((alert, i) => (
                <div 
                  key={alert.id}
                  className="animate-fade-slide-in"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <AlertItem 
                    alert={alert} 
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                    onNavigate={handleNavigate}
                  />
                </div>
              ))
            ) : (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No alerts</p>
                <p className="text-xs text-gray-400">You're all caught up!</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          {alerts.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button 
                onClick={() => handleNavigate('/alerts')}
                className="flex items-center justify-center gap-1 w-full text-sm text-violet-600 hover:text-violet-700 font-medium"
              >
                View all alerts
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
